import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { tap, map } from 'rxjs';
import { OutagesService, OutageLevels } from 'src/app/pages/outage/outage.service';
import { PlatformService } from 'universal';

@Injectable({
  providedIn: 'root',
})
export class OutageGuard {
  os = inject(OutagesService);
  router = inject(Router);
  ps = inject(PlatformService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (this.ps.isServer()) return true;

    const { activateIfNoOutage = true }: { activateIfNoOutage?: boolean } = route.data;

    // --- helpers ---
    const normalize = (s?: string | null) => s?.trim().toLowerCase() ?? '';
    const isAcceptedOutageLevel = (level?: string | null): boolean => {
      const n = normalize(level);
      return !!n && OutageLevels.some(accepted => accepted.toLowerCase() === n);
    };
    const hasAcceptedOutageIn = (outages?: Array<{ outageLevel?: string | null }>): boolean => {
      if (!outages || outages.length === 0) return false;
      return outages.some(o => isAcceptedOutageLevel(o?.outageLevel));
    };

    const call = this.os.getOutageInfo();

    return call.pipe(
      map(res => {
        // Determine whether an accepted outage is active
        const outageActive =
          res?.currentOutage?.outage === 'true' &&
          hasAcceptedOutageIn(res?.currentOutage?.outages);

        const emergencyActive =
          res?.allOutages?.emergency?.enabled === 'true' &&
          isAcceptedOutageLevel(res?.allOutages?.emergency?.outageLevel);

        const isOutageRoute = state.url.includes('/outage');

        if (activateIfNoOutage) {
          // For routes that should be available when there's no accepted outage
          const noActiveOutage = !(outageActive || emergencyActive);

          // Block `/outage` if there's no accepted outage
          if (noActiveOutage && isOutageRoute) {
            return this.router.navigateByUrl('/login');
          }

          // Force `/outage` from any other route if an accepted outage is active
          if (!noActiveOutage && !isOutageRoute) {
            return this.router.navigateByUrl('/outage');
          }

          return true;
        } else {
          // For routes (like `/outage`) that should only activate during an accepted outage
          const isActiveOutage = outageActive || emergencyActive;

          // No accepted outage → block `/outage`
          if (!isActiveOutage && isOutageRoute) {
            return this.router.navigateByUrl('/login');
          }

          // Accepted outage → force `/outage` from other routes
          if (isActiveOutage && !isOutageRoute) {
            
            return this.router.navigateByUrl('/outage');
          }

          return true;
        }
      }),
       );
  }
}
